import React from 'react';
import { Link } from 'react-router-dom';
import { navLinks, socialLinks, contactInfo } from '../data/mock';
import { Instagram, Youtube, Mail } from 'lucide-react';

const socialIcons = {
  Instagram,
  YouTube: Youtube
};

const Footer = () => {
  const logoUrl = "https://customer-assets.emergentagent.com/job_podcast-launch-1/artifacts/bdhzg2gi_Rosepod%20logo%20white%20color.png";

  return (
    <footer className="relative bg-[#04101C] overflow-hidden">
      {/* Gradient Accent Line */}
      <div className="h-1 bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <a
              href="#home"
              className="inline-block mb-4"
            >
              <img 
                src={logoUrl} 
                alt="Rosepod" 
                className="h-10 w-auto"
              />
            </a>
            <p className="text-[#F5FDFF]/60 text-sm mb-6 max-w-xs">
              The most vibrant full-stack podcast agency. From idea to global audience.
            </p>

            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social) => {
                const Icon = socialIcons[social.name];
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#F5FDFF]/5 flex items-center justify-center text-[#F5FDFF]/60 hover:bg-gradient-to-r hover:from-[#00C4CC] hover:to-[#00E08F] hover:text-[#04101C] transition-all duration-300"
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-[#F5FDFF] font-semibold mb-4">Navigation</h3>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.label}>
                  {link.isPage ? (
                    <Link
                      to={link.href}
                      className="text-[#F5FDFF]/60 hover:text-[#00E08F] transition-colors text-sm"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-[#F5FDFF]/60 hover:text-[#00E08F] transition-colors text-sm"
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Platform Badges */}
          <div>
            <h3 className="text-[#F5FDFF] font-semibold mb-4">Distribution Platforms</h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2 text-[#F5FDFF]/60 text-sm">
                <div className="w-6 h-6 rounded-full bg-[#1DB954]/20 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-3 h-3 text-[#1DB954]" fill="currentColor">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                </div>
                Spotify
              </div>
              <div className="flex items-center gap-2 text-[#F5FDFF]/60 text-sm">
                <div className="w-6 h-6 rounded-full bg-[#FF0000]/20 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-3 h-3 text-[#FF0000]" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </div>
                YouTube
              </div>
              <div className="flex items-center gap-2 text-[#F5FDFF]/60 text-sm">
                <div className="w-6 h-6 rounded-full bg-[#FA243C]/20 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-3 h-3 text-[#FA243C]" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                Apple Podcasts
              </div>
              <div className="flex items-center gap-2 text-[#F5FDFF]/60 text-sm">
                <div className="w-6 h-6 rounded-full bg-[#2BC5B4]/20 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-3 h-3 text-[#2BC5B4]" fill="currentColor">
                    <circle cx="12" cy="12" r="10"/>
                  </svg>
                </div>
                JioSaavn
              </div>
              <div className="flex items-center gap-2 text-[#F5FDFF]/60 text-sm">
                <div className="w-6 h-6 rounded-full bg-[#00A8E1]/20 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-3 h-3 text-[#00A8E1]" fill="currentColor">
                    <path d="M12 0C5.374 0 0 5.374 0 12s5.374 12 12 12 12-5.374 12-12S18.626 0 12 0z"/>
                  </svg>
                </div>
                Amazon Music
              </div>
              <div className="flex items-center gap-2 text-[#F5FDFF]/60 text-sm">
                <div className="w-6 h-6 rounded-full bg-[#E72C30]/20 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-3 h-3 text-[#E72C30]" fill="currentColor">
                    <circle cx="12" cy="12" r="10"/>
                  </svg>
                </div>
                Gaana
              </div>
              <div className="flex items-center gap-2 text-[#F5FDFF]/60 text-sm col-span-2">
                <div className="w-6 h-6 rounded-full bg-[#F8991C]/20 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-3 h-3 text-[#F8991C]" fill="currentColor">
                    <path d="M12 0C5.374 0 0 5.374 0 12s5.374 12 12 12 12-5.374 12-12S18.626 0 12 0z"/>
                  </svg>
                </div>
                Audible
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-[#F5FDFF] font-semibold mb-4">Get In Touch</h3>
            <p className="text-[#F5FDFF]/60 text-sm mb-4">
              Ready to start your podcast journey?
            </p>
            <a 
              href={`mailto:${contactInfo.email}`}
              className="flex items-center gap-3 text-[#00E08F] hover:text-[#00C4CC] transition-colors text-sm mb-4"
            >
              <Mail size={18} />
              {contactInfo.email}
            </a>
            <a 
              href="https://www.instagram.com/rosepodindia/?hl=en"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-[#F5FDFF]/60 hover:text-[#00E08F] transition-colors text-sm"
            >
              <Instagram size={18} />
              @rosepodindia
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-[#F5FDFF]/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#F5FDFF]/40 text-sm">
            © 2025 Rosepod. All rights reserved.
          </p>

          {/* Animated Equalizer */}
          <div className="flex items-end gap-1 h-6">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="w-1 rounded-full bg-gradient-to-t from-[#00C4CC] to-[#00E08F]"
                style={{
                  animation: `footerWave 1.2s ease-in-out infinite`,
                  animationDelay: `${i * 0.1}s`,
                  height: '30%'
                }}
              />
            ))}
          </div>

          <div className="flex gap-6 text-[#F5FDFF]/40 text-sm">
            <a href="#" className="hover:text-[#00E08F] transition-colors">Privacy</a>
            <a href="#" className="hover:text-[#00E08F] transition-colors">Terms</a>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes footerWave {
          0%, 100% { height: 30%; }
          50% { height: 100%; }
        }
      `}</style>
    </footer>
  );
};

export default Footer;
